import React from "react";
import styled, { keyframes, css } from "styled-components";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";

// Components
import FullButton from "../Buttons/FullButton";

// Assets
import AboutImg from "../../assets/svg/Logo.png";
import Exp3D from "../../assets/img/3dicons/year-of-experience-3d.png";
import Clients3D from "../../assets/img/3dicons/customer-satisfied-3d.png";
import Countries3D from "../../assets/img/3dicons/countries-served-3d.png";
import Lock3D from "../../assets/img/3dicons/lock-key-3d.png";

export default function AboutUs() {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <Wrapper id="about">
      <div className="gradientBg">
        <div className="container">
          {/* About Section */}
          <AboutSection className="flexSpaceCenter">
            <AboutLeft>
              <AboutLeftInner>
                <ImgWrapper className="flexCenter">
                  <img
                    className="radius16 shadow"
                    src={AboutImg}
                    alt="about us"
                  />
                </ImgWrapper>
              </AboutLeftInner>
            </AboutLeft>

            <AboutRight>
              <h4 className="font18 semiBold accent">Who We Are</h4>
              <h2 className="extraBold gradientText">About Our Company</h2>
              <p className="font16">
                We are a passionate team dedicated to delivering{" "}
                <strong>top-quality solutions</strong> for our clients. Our
                mission is to combine{" "}
                <em>creativity, technology, and strategy</em> to help businesses
                grow and achieve their goals. With a focus on{" "}
                <strong>innovation</strong> and{" "}
                <strong>customer success</strong>, we’ve built long-lasting
                partnerships across industries worldwide.
              </p>

              <ButtonsRow
                className="flexNullCenter"
                style={{ margin: "40px 0" }}
              >
                <div style={{ width: "220px" }}>
                  <FullButton
                    title="Learn More"
                    action={() => alert("clicked")}
                  />
                </div>
                <div style={{ width: "220px", marginLeft: "20px" }}>
                  <FullButton
                    title="Contact Us"
                    action={() => alert("clicked")}
                    border
                  />
                </div>
              </ButtonsRow>
            </AboutRight>
          </AboutSection>

          {/* Trust Counters */}
          <TrustRow ref={ref}>
            <TrustItem inView={inView} delay="0s">
              <img
                src={Exp3D}
                alt="Years of Experience"
                className="trust-icon"
              />
              <h3>
                {inView && (
                  <CountUp end={15} duration={3} delay={0} />
                )}
                +
              </h3>
              <p>Years of Experience</p>
            </TrustItem>

            <TrustItem inView={inView} delay="0.2s">
              <img
                src={Clients3D}
                alt="Happy Clients"
                className="trust-icon"
              />
              <h3>
                {inView && (
                  <CountUp end={1000} duration={3} delay={0.2} />
                )}
                +
              </h3>
              <p>Happy Clients</p>
            </TrustItem>

            <TrustItem inView={inView} delay="0.4s">
              <img
                src={Countries3D}
                alt="Countries Served"
                className="trust-icon"
              />
              <h3>
                {inView && (
                  <CountUp end={25} duration={3} delay={0.4} />
                )}
                +
              </h3>
              <p>Countries Served</p>
            </TrustItem>

            <TrustItem inView={inView} delay="0.6s">
              <img
                src={Lock3D}
                alt="Secure & Reliable"
                className="trust-icon"
              />
              <h3>
                {inView && (
                  <CountUp end={99} duration={3} delay={0.6} />
                )}
                %
              </h3>
              <p>Secure & Reliable</p>
            </TrustItem>
          </TrustRow>
        </div>
      </div>
    </Wrapper>
  );
}

// ================= STYLES =================

const Wrapper = styled.section`
  width: 100%;
  background: linear-gradient(180deg, #f9f9f9 0%, #fff 100%);
  padding: 80px 0 120px;

  @media (max-width: 768px) {
    padding: 50px 0 80px;
  }
`;

/* About Section */
const AboutSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 60px;

  @media (max-width: 1160px) {
    margin-bottom: 40px;
  }
  @media (max-width: 860px) {
    flex-direction: column;
    margin-bottom: 30px;
    text-align: center;
  }
`;

/* Floating animation */
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-12px); }
  100% { transform: translateY(0px); }
`;

const AboutLeftInner = styled.div`
  width: 100%;
`;

const ImgWrapper = styled.div`
  width: 100%;
  padding: 0 8%;
  img {
    width: 100%;
    border-radius: 16px;
    box-shadow: 0px 12px 32px rgba(0, 0, 0, 0.25);
    transition: transform 0.4s ease, box-shadow 0.4s ease;
    animation: ${float} 5s ease-in-out infinite;
  }
  img:hover {
    transform: scale(1.07) rotate(2deg);
    box-shadow: 0px 20px 40px rgba(118, 32, 255, 0.35);
  }
`;

const AboutRight = styled.div`
  width: 50%;
  padding-left: 40px;

  h4.accent {
    color: #7620ff;
    margin-bottom: 12px;
  }
  h2.gradientText {
    font-size: 2.8rem;
    background: linear-gradient(90deg, #7620ff, #34d058);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 20px;
  }
  p {
    margin-top: 20px;
    color: #555;
    line-height: 1.7;
  }

  @media (max-width: 860px) {
    width: 90%;
    order: 1;
    text-align: center;
    padding-left: 0;
  }
`;

const ButtonsRow = styled.div`
  @media (max-width: 860px) {
    justify-content: center;
    gap: 20px;
  }
`;

/* Fade-in Animation */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* Trust Section */
const TrustRow = styled.div`
  margin-top: 80px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 25px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

/* Trust Items with stagger delay */
const TrustItem = styled.div`
  text-align: center;
  background: rgba(255, 255, 255, 0.95);
  padding: 35px 25px;
  border-radius: 20px;
  backdrop-filter: blur(12px);
  box-shadow: 0px 10px 25px rgba(0, 0, 0, 0.08);
  transition: transform 0.4s ease, box-shadow 0.4s ease, background 0.4s ease;
  opacity: 0;
  transform: translateY(30px);

  ${({ inView, delay }) =>
    inView &&
    css`
      animation: ${fadeInUp} 0.8s ease forwards;
      animation-delay: ${delay};
    `}

  &:hover {
    transform: translateY(-12px) scale(1.05);
    background: linear-gradient(
      135deg,
      rgba(118, 32, 255, 0.08),
      rgba(52, 208, 88, 0.08)
    );
    box-shadow: 0px 18px 40px rgba(118, 32, 255, 0.2);
  }

  .trust-icon {
    width: 80px;
    height: 80px;
    object-fit: contain;
    margin-bottom: 15px;
    animation: ${float} 4s ease-in-out infinite;
  }

  h3 {
    font-size: 42px;
    font-weight: 800;
    background: linear-gradient(90deg, #28a745, #34d058);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 8px;
    transition: text-shadow 0.3s ease;
  }

  &:hover h3 {
    text-shadow: 0px 0px 12px rgba(52, 208, 88, 0.5);
  }

  p {
    font-size: 16px;
    color: #444;
    font-weight: 500;
  }
`;

const AboutLeft = styled.div`
  position: relative;
  width: 50%;
  @media (max-width: 860px) {
    width: 90%;
    order: 2;
  }
`;
